// src/encounter/risk/lib/risk-wire.ts
// Local <-> wire mapping for the EncounterMetaData Fall/Pain Assessment endpoints (data-layer
// handoff, 2026-09-13). The wire shapes are, by design, congruent with the local
// `FallAssessmentEntry` / `PainAssessmentEntry` shapes in `risk-assessment-types.ts` (this repo's
// local types were written FROM the handoff, not the other way round) — so encode/decode is
// mostly identity, with one real difference: `createdOn` is OMITTED from the wire entirely when
// unset (never sent as `null`), so encode strips the key rather than nulling it.
import type { FallAssessmentEntry, PainAssessmentEntry } from './risk-assessment-types';

export interface FallAssessmentEnvelope {
  readonly fallAssessments: readonly FallAssessmentEntry[];
}
export interface PainAssessmentEnvelope {
  readonly painAssessments: readonly PainAssessmentEntry[];
}

/** Drop `createdOn` entirely when unset — the wire omits the key rather than sending `null`. */
function encodeFallEntry(entry: FallAssessmentEntry): Omit<FallAssessmentEntry, 'createdOn'> & { createdOn?: string } {
  const { createdOn, ...rest } = entry;
  return createdOn === undefined ? rest : { ...rest, createdOn };
}

function encodePainEntry(entry: PainAssessmentEntry): Omit<PainAssessmentEntry, 'createdOn'> & { createdOn?: string } {
  const { createdOn, ...rest } = entry;
  return createdOn === undefined ? rest : { ...rest, createdOn };
}

export function encodeFallEnvelope(list: readonly FallAssessmentEntry[]): FallAssessmentEnvelope {
  return { fallAssessments: list.map(encodeFallEntry) as unknown as FallAssessmentEntry[] };
}

export function encodePainEnvelope(list: readonly PainAssessmentEntry[]): PainAssessmentEnvelope {
  return { painAssessments: list.map(encodePainEntry) as unknown as PainAssessmentEntry[] };
}

/** Defensive decode: an absent/malformed envelope decodes to an empty list (the caller — the
 *  network hook — is the one that decides an empty list means "keep the local placeholder",
 *  i.e. merge-not-replace; this function only ever describes what the WIRE said). */
export function decodeFallEnvelope(json: unknown): FallAssessmentEntry[] {
  if (!json || typeof json !== 'object') return [];
  const list = (json as { fallAssessments?: unknown }).fallAssessments;
  return Array.isArray(list) ? (list as FallAssessmentEntry[]) : [];
}

export function decodePainEnvelope(json: unknown): PainAssessmentEntry[] {
  if (!json || typeof json !== 'object') return [];
  const list = (json as { painAssessments?: unknown }).painAssessments;
  return Array.isArray(list) ? (list as PainAssessmentEntry[]) : [];
}
